import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { bulkAPI, productsAPI } from '../services/api';
import { notify } from '../utils/notify';
import { HiCollection, HiPencil, HiTrash, HiRefresh, HiSearch, HiSortAscending, HiSortDescending, HiCloudDownload, HiPause, HiDownload } from 'react-icons/hi';
import ProductDetailsModal from '../components/ProductDetailsModal';
import ProductEditModal from '../components/ProductEditModal';
import ConfirmModal from '../components/ConfirmModal';
import PublishChecklistModal from '../components/PublishChecklistModal';
import { useTranslation } from 'react-i18next';
import { useCarga } from '../hooks/useCarga';
import ErrorAlCargar from '../components/ErrorAlCargar';
import { PRODUCT_IMAGE_FALLBACK } from '../utils/imagePlaceholder';
import { comisionDe } from '../utils/comision';
import { mensajeDeError } from '../utils/apiError';
import { desdeImportado } from '../utils/productoVista';
import { dinero } from '../utils/economia';
import { estadoDelAnuncio, sePuedeRetirar } from '../utils/estadoAnuncio';


export default function ImportedProducts() {
    const [products, setProducts] = useState([]);

    // Selección múltiple para cambiar el margen o el estado de varios a la vez.
    // Van como operación en segundo plano, igual que la importación: no porque
    // tarden, sino para que quede registro de qué se tocó y con qué resultado.
    const [seleccion, setSeleccion] = useState(new Set());
    const [enviandoTanda, setEnviandoTanda] = useState(false);
    const [margenTanda, setMargenTanda] = useState(30);

    const alternarSeleccion = (id) =>
        setSeleccion((antes) => {
            const copia = new Set(antes);
            if (copia.has(id)) copia.delete(id);
            else copia.add(id);
            return copia;
        });

    // Retirar de la venta: el anuncio se termina en el destino y el producto
    // queda en pausa. En ese orden — si el destino falla, no se marca nada.
    const [retirando, setRetirando] = useState(new Set());

    const retirar = async (item) => {
        setRetirando((antes) => new Set([...antes, item.id]));
        try {
            const res = await productsAPI.withdraw(item.id);
            notify.success(
                res.data?.data?.yaEstaba
                    ? t('imported_products.withdraw_already')
                    : t('imported_products.withdraw_done')
            );
            fetchProducts();
        } catch (error) {
            notify.error(mensajeDeError(error, t('imported_products.withdraw_failed')));
        } finally {
            setRetirando((antes) => {
                const copia = new Set(antes);
                copia.delete(item.id);
                return copia;
            });
        }
    };

    // Solo el margen, y a propósito.
    //
    // Aquí había también un botón «Pausar» que escribía `status: 'paused'` en
    // una columna de nuestra base de datos y no tocaba eBay: el anuncio seguía
    // publicado y en venta, con la etiqueta «pausado» en tu lista. Lo puse yo
    // sin comprobar el endpoint. Pausar de verdad exige retirar la oferta en
    // eBay, y eso se hace en `destino.service.js`, no escribiendo una columna.
    // De lo marcado, lo que de verdad tiene un anuncio que retirar. Un
    // producto sin publicar no se toca: marcarlo «pausado» lo sacaría de «sin
    // publicar» sin que nada haya pasado en la tienda.
    const retirables = products.filter((p) => seleccion.has(p.id) && sePuedeRetirar(p));

    // Marcar y desmarcar de golpe lo que hay en pantalla.
    const todosMarcados = products.length > 0 && products.every((p) => seleccion.has(p.id));
    const alternarTodos = () =>
        setSeleccion(todosMarcados ? new Set() : new Set(products.map((p) => p.id)));

    const pausarSeleccionados = async () => {
        const ids = retirables.map((p) => p.id);
        if (ids.length === 0) return;

        setEnviandoTanda(true);
        try {
            await bulkAPI.bulkStatusChange({ productIds: ids, status: 'paused' });
            setSeleccion(new Set());
            notify.success(t('imported_products.bulk_withdraw_started', { count: ids.length }));
        } catch (error) {
            notify.error(mensajeDeError(error, t('imported_products.bulk_failed')));
        } finally {
            setEnviandoTanda(false);
        }
    };

    const aplicarMargen = async () => {
        const ids = [...seleccion];
        if (ids.length === 0) return;

        setEnviandoTanda(true);
        try {
            await bulkAPI.bulkPriceUpdate({ productIds: ids, markupPercentage: Number(margenTanda) });
            setSeleccion(new Set());
            notify.success(t('imported_products.bulk_started', { count: ids.length }));
        } catch (error) {
            notify.error(mensajeDeError(error, t('imported_products.bulk_failed')));
        } finally {
            setEnviandoTanda(false);
        }
    };
    const { cargando: loading, error, ejecutar, reintentar } = useCarga();
    // El filtro se puede traer en la dirección: `?filtro=published`.
    //
    // Hace falta porque el embudo del Panel enlaza aquí con un filtro puesto, y
    // un enlace que lleva a la lista completa obliga a repetir a mano el filtro
    // del número que acabas de pulsar. Solo se admiten los tres que existen.
    const FILTROS = ['all', 'unpublished', 'published'];
    const pedido = new URLSearchParams(useLocation().search).get('filtro');
    const [filter, setFilter] = useState(FILTROS.includes(pedido) ? pedido : 'all');
    const [search, setSearch] = useState('');
    const [terminoBuscado, setTerminoBuscado] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortDir, setSortDir] = useState('desc');
    const [total, setTotal] = useState(0);
    const [viewModal, setViewModal] = useState(null);
    const [editModal, setEditModal] = useState(null);
    const [productToDelete, setProductToDelete] = useState(null);
    const [productoAPublicar, setProductoAPublicar] = useState(null);
    const [comprobandoAnuncios, setComprobandoAnuncios] = useState(false);
    const [exportando, setExportando] = useState(false);
    const { t } = useTranslation();


    useEffect(() => {
        fetchProducts();
    }, [filter, terminoBuscado, sortBy, sortDir]);

    // Al entrar se pregunta a la tienda por los anuncios publicados. El
    // servidor no repite la consulta si ya la hizo hace poco, asi que entrar y
    // salir de la pagina no castiga la API.
    useEffect(() => {
        let vigente = true;

        productsAPI
            .refreshListings()
            .then((res) => {
                if (vigente && res.data.data.consultados > 0) fetchProducts();
            })
            .catch(() => {
                // Que no se pueda consultar no rompe la pagina: las tarjetas
                // enseñaran el ultimo estado conocido, con su fecha.
            });

        return () => {
            vigente = false;
        };
    }, []);

    // La búsqueda se lanza sola al dejar de escribir, sin botón de por medio.
    useEffect(() => {
        const id = setTimeout(() => setTerminoBuscado(search.trim()), 350);
        return () => clearTimeout(id);
    }, [search]);

    const fetchProducts = () =>
        ejecutar(async () => {
            const params = { sortBy, sortDir };
            if (filter === 'published') params.published = 'true';
            if (filter === 'unpublished') params.published = 'false';
            if (terminoBuscado) params.search = terminoBuscado;

            const response = await productsAPI.getImported(params);
            setProducts(response.data.data.products || []);
            setTotal(response.data.data.pagination?.total ?? 0);
        });

    const handleView = (product) => {
        setViewModal(product);
    };

    const handleEdit = (product) => {
        setEditModal(product);
    };

    // Publicar no dispara nada: abre la comprobación previa, que dice de una vez
    // todo lo que eBay va a exigir y deja rellenar lo que falte.
    const handleSync = (product) => setProductoAPublicar(product);

    /**
     * Descarga el archivo que importa Shopify, con **lo marcado y solo eso**.
     *
     * La primera versión de esto se llevaba el catálogo entero sin preguntar, y
     * estaba mal por la misma razón por la que publicar es producto a producto:
     * nadie quiere mandar a su tienda todo lo que tiene importado de una vez.
     * Ahora vive donde viven las demás acciones sobre lo marcado, al lado de
     * «Aplicar el margen».
     *
     * Dos cuidados que no se ven:
     *
     * 1. La respuesta es un **archivo**, no JSON, así que la petición pide
     *    `blob`. Eso significa que cuando el servidor responde con un error,
     *    `error.response.data` también es un blob —el JSON del error, en
     *    binario—, y `mensajeDeError` no sabría leerlo. Hay que abrirlo antes.
     * 2. El recuento viaja en cabeceras porque el cuerpo es el archivo. Si
     *    quedaron productos fuera, se dicen: un número incompleto presentado
     *    como el total es justo lo que aquí no se hace.
     */
    const exportarAShopify = async () => {
        const ids = [...seleccion];
        if (ids.length === 0) return;

        setExportando(true);
        try {
            const res = await productsAPI.exportarShopify(ids);

            const exportados = Number(res.headers['x-exportados'] || 0);
            const sinPrecio = Number(res.headers['x-sin-precio'] || 0);

            const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
            const enlace = document.createElement('a');
            enlace.href = url;
            enlace.download = `shippingfull-shopify-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(enlace);
            enlace.click();
            enlace.remove();
            URL.revokeObjectURL(url);

            notify.success(
                sinPrecio > 0
                    ? t('imported_products.export_done_partial', { count: exportados, sinPrecio })
                    : t('imported_products.export_done', { count: exportados })
            );
        } catch (error) {
            // El error viene envuelto en un blob por haber pedido binario.
            // Sin desenvolverlo, la pantalla diría «Algo falló en el servidor»
            // en vez de «ninguno de tus productos tiene precio de venta».
            if (error?.response?.data instanceof Blob) {
                try {
                    error.response.data = JSON.parse(await error.response.data.text());
                } catch {
                    // No era JSON. Se queda como estaba y manda el respaldo.
                }
            }
            notify.error(mensajeDeError(error, t('imported_products.export_failed')));
        } finally {
            setExportando(false);
        }
    };

    const comprobarAnuncios = async () => {
        setComprobandoAnuncios(true);
        try {
            const res = await productsAPI.refreshListings(true);
            await fetchProducts();

            // El servidor devuelve consultados, fallidos y omitidos, y aquí
            // sólo se leía el primero: con un anuncio que fallaba salía «Se
            // comprobaron 0 anuncio(s)» en verde, como si hubiera ido bien.
            // Un fallo no se pinta como un éxito.
            const { consultados, fallidos } = res.data.data;

            if (fallidos > 0 && consultados > 0) {
                notify.info(t('imported_products.listings_checked_partial', { n: consultados, fallidos }));
            } else if (fallidos > 0) {
                notify.error(t('imported_products.listings_none_checked', { fallidos }));
            } else if (consultados > 0) {
                notify.success(t('imported_products.listings_checked', { n: consultados }));
            } else {
                notify.info(t('imported_products.listings_nothing_to_check'));
            }
        } catch (error) {
            notify.error(mensajeDeError(error, t('imported_products.listings_check_failed')));
        } finally {
            setComprobandoAnuncios(false);
        }
    };

    const handleDeleteClick = (product) => {
        setProductToDelete(product);
    };

    const confirmDelete = async () => {
        if (!productToDelete) return;

        try {
            notify.loading(t('imported_products.deleting'));
            await productsAPI.deleteImported(productToDelete.id);
            notify.dismiss();
            notify.success(t('imported_products.delete_success'));
            fetchProducts();
        } catch (error) {
            notify.dismiss();
            notify.error(t('imported_products.delete_failed'));
        } finally {
            setProductToDelete(null);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/4 animate-pulse"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="h-72 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse"></div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="page-title flex items-center gap-2">
                        <HiCollection className="w-8 h-8 text-primary-500" />
                        {t('imported_products.title')}
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">
                        {t('imported_products.subtitle')}
                    </p>
                </div>

                {/* Aquí estuvo «Exportar para Shopify», y era un error: se
                    llevaba el catálogo entero sin preguntar. Publicar es
                    producto a producto, y exportar tenía que serlo también.
                    Ahora vive en la barra de lo marcado, con las demás. */}
                <div className="flex items-center gap-2">
                    {['all', 'unpublished', 'published'].map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f
                                ? 'bg-primary-500 text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                        >
                            {t(`imported_products.filter_${f}`)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Buscar y ordenar. El orden lo aplica el servidor sobre todo el
                catálogo, no solo sobre lo que hay cargado en pantalla. */}
            <div className="card flex flex-col lg:flex-row gap-3 lg:items-center">
                <div className="flex-1 relative">
                    <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('imported_products.search_placeholder')}
                        className="input pl-10"
                    />
                </div>

                <div className="flex items-center gap-2">
                    <label htmlFor="sortBy" className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {t('imported_products.sort_by')}
                    </label>
                    <select
                        id="sortBy"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="input w-auto"
                    >
                        <option value="created_at">{t('imported_products.sort_recent')}</option>
                        <option value="title">{t('imported_products.sort_title')}</option>
                        <option value="cost">{t('imported_products.cost')}</option>
                        <option value="price">{t('imported_products.price')}</option>
                        <option value="profit">{t('imported_products.profit')}</option>
                    </select>

                    <button
                        type="button"
                        onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                        title={t(sortDir === 'asc' ? 'imported_products.sort_asc' : 'imported_products.sort_desc')}
                        className="btn-secondary btn-sm"
                    >
                        {sortDir === 'asc' ? <HiSortAscending className="w-5 h-5" /> : <HiSortDescending className="w-5 h-5" />}
                    </button>
                </div>

                <button
                    type="button"
                    onClick={comprobarAnuncios}
                    disabled={comprobandoAnuncios}
                    title={t('imported_products.check_listings')}
                    className="btn-secondary btn-sm disabled:opacity-50"
                >
                    <HiCloudDownload className={`w-5 h-5 ${comprobandoAnuncios ? 'animate-pulse' : ''}`} />
                </button>

                {/* Marcar todo de una vez. Sin esto, «quiero exportarlos todos»
                    obligaba a pulsar trece casillas, y era la razón por la que
                    el botón de exportar se llevaba el catálogo entero sin
                    preguntar.

                    Dice cuántos marca, y no «todos», porque marca los de esta
                    página: con más productos de los que caben, «todos» sería
                    falso. */}
                {products.length > 0 && (
                    <label className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 cursor-pointer whitespace-nowrap lg:ml-2">
                        <input
                            type="checkbox"
                            checked={todosMarcados}
                            onChange={alternarTodos}
                            className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-primary-500 focus:ring-primary-500"
                        />
                        {t('imported_products.select_page', { count: products.length })}
                    </label>
                )}

                <p className="text-sm text-slate-500 dark:text-slate-400 lg:ml-2 whitespace-nowrap">
                    {t('imported_products.showing', { count: products.length, total })}
                </p>
            </div>

            {/* Lo marcado y qué hacer con ello. */}
            {seleccion.size > 0 && (
                <div className="card flex flex-wrap items-center gap-3 !py-3 sticky top-20 z-20">
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                        {t('imported_products.selected', { count: seleccion.size })}
                    </span>

                    <div className="flex items-center gap-2">
                        <label htmlFor="margenTanda" className="text-sm text-slate-500">
                            {t('imported_products.bulk_markup')}
                        </label>
                        <input
                            id="margenTanda"
                            type="number"
                            min="0"
                            max="500"
                            value={margenTanda}
                            onChange={(e) => setMargenTanda(e.target.value)}
                            className="input w-20"
                        />
                        <button
                            type="button"
                            onClick={aplicarMargen}
                            disabled={enviandoTanda}
                            className="btn-secondary btn-sm"
                        >
                            {t('imported_products.bulk_apply_markup')}
                        </button>
                    </div>

                    {retirables.length > 0 && (
                        <button
                            type="button"
                            onClick={pausarSeleccionados}
                            disabled={enviandoTanda}
                            className="btn-secondary btn-sm"
                        >
                            {t('imported_products.bulk_withdraw', { count: retirables.length })}
                        </button>
                    )}

                    {/* Aquí, y no arriba. Se lleva lo marcado, dice cuántos son
                        en su propia etiqueta, y desaparece cuando no hay nada
                        marcado — igual que el resto de esta barra. */}
                    <button
                        type="button"
                        onClick={exportarAShopify}
                        disabled={exportando || enviandoTanda}
                        title={t('imported_products.export_shopify_hint')}
                        className="btn-secondary btn-sm disabled:opacity-50"
                    >
                        <HiDownload className={`w-4 h-4 mr-1 ${exportando ? 'animate-pulse' : ''}`} />
                        {t('imported_products.export_shopify', { count: seleccion.size })}
                    </button>

                    <button
                        type="button"
                        onClick={() => setSeleccion(new Set())}
                        className="btn-secondary btn-sm"
                    >
                        {t('imported_products.clear_selection')}
                    </button>

                    <Link to="/bulk" className="text-sm text-primary-500 hover:underline ml-auto">
                        {t('imported_products.see_operations')}
                    </Link>
                </div>
            )}

            {/* Products Grid */}
            {error ? (
                <ErrorAlCargar error={error} onReintentar={reintentar} />
            ) : products.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map((item) => (
                        <div
                            key={item.id}
                            className={`card-hover group !border-l-4 relative ${estadoDelAnuncio(item).borde}`}
                        >
                            <label className="absolute top-3 right-3 z-10 flex items-center justify-center w-7 h-7 rounded-md bg-white/90 dark:bg-slate-900/90 border border-slate-300 dark:border-slate-600 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={seleccion.has(item.id)}
                                    onChange={() => alternarSeleccion(item.id)}
                                    aria-label={t('imported_products.select_product')}
                                    className="w-4 h-4 accent-primary-500 cursor-pointer"
                                />
                            </label>
                            <div className="flex items-start gap-4">
                                <div className="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden flex-shrink-0">
                                    <img
                                        src={item.main_image || PRODUCT_IMAGE_FALLBACK}
                                        alt={item.custom_title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-medium text-slate-900 dark:text-white line-clamp-2">
                                        {item.custom_title || item.original_title}
                                    </h3>
                                    <p className="text-sm text-slate-500 mt-1">
                                        {item.store?.store_name} • {item.store?.platform}
                                    </p>
                                </div>
                            </div>

                            {(() => {
                                // El coste que importa es el puesto en destino. Si no
                                // se conoce el envío se dice, en lugar de dar por
                                // buena una ganancia a la que le falta un sumando.
                                const envioConocido = item.shipping_cost !== null && item.shipping_cost !== undefined;
                                const costoTotal = Number(item.cost_price || 0) + (envioConocido ? Number(item.shipping_cost) : 0);
                                // Ya es la ganancia DESPUÉS de la comisión del destino.
                                // `null` significa que esa comisión no se sabe, y no se
                                // pinta como $0.00: cero sería afirmar que no se gana nada.
                                const gananciaConocida = item.profit_margin !== null && item.profit_margin !== undefined;
                                const ganancia = gananciaConocida ? Number(item.profit_margin) : null;

                                return (
                                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div>
                                                <p className="text-xs text-slate-500">
                                                    {envioConocido ? t('imported_products.landed_cost') : t('imported_products.cost')}
                                                </p>
                                                <p className="font-semibold text-slate-900 dark:text-white">
                                                    {dinero(costoTotal)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-500">{t('imported_products.price')}</p>
                                                <p className="font-semibold text-slate-900 dark:text-white">
                                                    {dinero(item.custom_price)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-500">
                                                    {envioConocido ? t('imported_products.profit') : t('product_search.profit_no_shipping')}
                                                </p>
                                                {gananciaConocida ? (
                                                    <p className={`font-semibold ${ganancia > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                        {dinero(ganancia)}
                                                    </p>
                                                ) : (
                                                    <p className="font-semibold text-slate-400" title={t('imported_products.profit_unknown_fee')}>
                                                        —
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {envioConocido && (
                                            <p className="mt-2 text-[11px] text-slate-400 text-center">
                                                {t('imported_products.includes_shipping', { amount: dinero(item.shipping_cost) })}
                                            </p>
                                        )}
                                        <p className="mt-1 text-[11px] text-slate-400 text-center">
                                            {gananciaConocida
                                                ? t('imported_products.profit_after_fee')
                                                : t('imported_products.profit_unknown_fee')}
                                        </p>
                                    </div>
                                );
                            })()}

                            <div className="flex items-center justify-between mt-4">
                                {(() => {
                                    const anuncio = estadoDelAnuncio(item);
                                    return (
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`badge ${anuncio.tono}`}>
                                                {anuncio.clave === 'desconocido'
                                                    ? anuncio.crudo
                                                    : t(`imported_products.estado_${anuncio.clave}`)}
                                            </span>
                                            {/* «Quedan 0 disponibles» y «no hemos
                                                preguntado cuántas quedan» son
                                                cosas opuestas, y el `?? 0` que
                                                había aquí las escribía igual.
                                                `listing_available_quantity` se
                                                deja en nulo a propósito cuando
                                                la consulta a eBay no se pudo
                                                hacer (products.routes.js:915).
                                                Sin ese dato se dice solo lo que
                                                se sabe: cuántas se vendieron. */}
                                            {item.listing_sold_quantity > 0 && (
                                                <span className="text-xs text-slate-500">
                                                    {item.listing_available_quantity === null || item.listing_available_quantity === undefined
                                                        ? t('imported_products.sold_only', { sold: item.listing_sold_quantity })
                                                        : t('imported_products.sold_units', {
                                                            sold: item.listing_sold_quantity,
                                                            left: item.listing_available_quantity
                                                        })}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })()}
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => handleView(item)}
                                        className="icon-view"
                                        title={t('imported_products.view')}
                                    >
                                        <HiSearch className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleEdit(item)}
                                        className="icon-edit"
                                        title={t('imported_products.edit')}
                                    >
                                        <HiPencil className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleSync(item)}
                                        disabled={estadoDelAnuncio(item).aLaVenta}
                                        className="icon-publish"
                                        title={
                                            estadoDelAnuncio(item).aLaVenta
                                                ? t('imported_products.already_published')
                                                : item.external_product_id
                                                    ? t('imported_products.republish')
                                                    : t('imported_products.publish')
                                        }
                                    >
                                        <HiRefresh className="w-4 h-4" />
                                    </button>
                                    {sePuedeRetirar(item) && (
                                        <button
                                            onClick={() => retirar(item)}
                                            disabled={retirando.has(item.id)}
                                            className="icon-edit"
                                            title={t('imported_products.withdraw')}
                                        >
                                            <HiPause className="w-4 h-4" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDeleteClick(item)}
                                        className="icon-delete"
                                        title={t('imported_products.delete')}
                                    >
                                        <HiTrash className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="card text-center py-16">
                    <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <HiCollection className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                        {terminoBuscado ? t('imported_products.no_results_title') :
                         filter === 'published' ? t('imported_products.no_published_title') :
                         filter === 'unpublished' ? t('imported_products.no_unpublished_title') :
                         t('imported_products.no_products_title')}
                    </h3>
                    <p className="text-slate-500 max-w-md mx-auto">
                        {terminoBuscado ? t('imported_products.no_results_desc') :
                         filter === 'published' ? t('imported_products.no_published_desc') :
                         filter === 'unpublished' ? t('imported_products.no_unpublished_desc') : 
                         filter === 'draft' ? t('imported_products.no_draft_products_desc') :
                         filter === 'paused' ? t('imported_products.no_paused_products_desc') :
                         t('imported_products.no_products_desc')}
                    </p>
                </div>
            )}

            {/* Modals */}
            {viewModal && (
                <ProductDetailsModal
                    vista={desdeImportado(viewModal)}
                    comision={comisionDe(viewModal.store?.platform, viewModal.store?.settings)}
                    onClose={() => setViewModal(null)}
                />
            )}
            {editModal && (
                <ProductEditModal
                    product={editModal}
                    onClose={() => setEditModal(null)}
                    onSave={fetchProducts}
                />
            )}
            {productoAPublicar && (
                <PublishChecklistModal
                    product={productoAPublicar}
                    onClose={() => setProductoAPublicar(null)}
                    onPublished={() => {
                        setProductoAPublicar(null);
                        notify.success(t('imported_products.publish_success'));
                        fetchProducts();
                    }}
                />
            )}
            <ConfirmModal
                isOpen={!!productToDelete}
                onClose={() => setProductToDelete(null)}
                onConfirm={confirmDelete}
                title={t('common.confirm_delete_title', '¿Eliminar este registro?')}
                message={t('imported_products.delete_confirm', '¿Estás seguro de que deseas eliminar este producto?')}
                confirmText={t('common.delete', 'Eliminar')}
            />
        </div>
    );
}
